import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, GraduationCap, Plus, Star, Trash2, User, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ACCOUNT_TYPES,
  MAX_ROLES,
  useAccountProfile,
  useProfileManager,
  useSwitchRole,
  type AccountType,
} from "@/hooks/use-account-type";
import { canAddProfile } from "@/lib/role-access";

export const Route = createFileRoute("/_app/profiles")({
  component: ProfilesPage,
  head: () => ({
    meta: [
      { title: "Gestione profili · Lumen" },
      {
        name: "description",
        content:
          "Aggiungi, rinomina o rimuovi i profili studente, professore e genitore, scegli quello predefinito e mantieni preferenze separate per ciascuno.",
      },
      { property: "og:title", content: "Gestione profili · Lumen" },
      { property: "og:description", content: "Gestisci i tuoi profili Lumen e le preferenze di ognuno." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const ICONS: Record<AccountType, typeof User> = { student: User, teacher: GraduationCap, parent: Users };

function ProfilesPage() {
  const { t } = useTranslation();
  const account = useAccountProfile();
  const manager = useProfileManager();
  const switchRole = useSwitchRole();
  const [renaming, setRenaming] = useState<AccountType | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>, okKey = "roleui.switch.saved") => {
    setBusy(true);
    try {
      await fn();
      toast.success(t(okKey));
    } catch (e) {
      const code = e instanceof Error ? e.message : String(e);
      const map: Record<string, string> = {
        duplicate: t("roleui.manage.duplicate"),
        minor: t("roleui.manage.minor"),
        max: t("roleui.manage.limit"),
        last_profile: t("roleui.manage.lastProfile"),
      };
      toast.error(map[code] ?? code);
    } finally {
      setBusy(false);
    }
  };

  const missing = ACCOUNT_TYPES.filter((r) => !account.roles.includes(r));

  return (
    <div className="space-y-4 pb-24">
      <PageHeader title={t("roleui.manage.title")} subtitle={t("roleui.manage.subtitle")} />

      <div className="space-y-3">
        {account.roles.map((r) => {
          const Icon = ICONS[r];
          const name = account.labels[r] ?? t(`teacher.role.${r}`);
          return (
            <Card key={r} className="card-soft space-y-3 border-0 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-semibold">{name}</p>
                  <p className="break-words text-xs text-muted-foreground">{t(`teacher.role.${r}Desc`)}</p>
                </div>
                {r === account.active && <Badge className="shrink-0">{t("roleui.manage.active")}</Badge>}
                {r === account.defaultRole && (
                  <Badge variant="secondary" className="shrink-0 gap-1">
                    <Star className="h-3 w-3" aria-hidden />
                    {t("roleui.manage.default")}
                  </Badge>
                )}
              </div>

              {renaming === r ? (
                <div className="flex flex-wrap gap-2">
                  <Input
                    value={draft}
                    maxLength={40}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t("roleui.manage.namePlaceholder")}
                    aria-label={t("roleui.manage.rename")}
                    className="min-w-0 flex-1"
                  />
                  <Button
                    size="sm"
                    className="min-h-9"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await manager.renameProfile(r, draft);
                        setRenaming(null);
                      })
                    }
                  >
                    {t("roleui.manage.save")}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {r !== account.active && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="min-h-9"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () => switchRole(r),
                          "roleui.switch.saved",
                        )
                      }
                    >
                      <Check className="mr-1.5 h-4 w-4" aria-hidden />
                      {t("roleui.switch.switchTo", { role: name })}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-9"
                    disabled={busy}
                    onClick={() => {
                      setRenaming(r);
                      setDraft(account.labels[r] ?? "");
                    }}
                  >
                    {t("roleui.manage.rename")}
                  </Button>
                  {r !== account.defaultRole && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="min-h-9"
                      disabled={busy}
                      onClick={() => void run(() => manager.setDefaultProfile(r))}
                    >
                      <Star className="mr-1.5 h-4 w-4" aria-hidden />
                      {t("roleui.manage.setDefault")}
                    </Button>
                  )}
                  {account.roles.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="min-h-9 text-destructive"
                      disabled={busy}
                      onClick={() => {
                        if (!window.confirm(t("roleui.manage.removeConfirm"))) return;
                        void run(() => manager.removeProfile(r));
                      }}
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
                      {t("roleui.manage.remove")}
                    </Button>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="card-soft space-y-3 border-0 p-4">
        <div>
          <h2 className="text-sm font-semibold">{t("roleui.manage.addTitle")}</h2>
          <p className="break-words text-xs text-muted-foreground">
            {account.isAdult
              ? t("roleui.switch.addHint")
              : t("roleui.manage.minor")}
          </p>
        </div>

        <label className="flex items-start gap-2 rounded-2xl bg-muted/60 p-3">
          <Checkbox
            checked={account.isAdult}
            disabled={busy}
            onCheckedChange={(v) => void run(() => manager.setAdult(v === true))}
            aria-label={t("roleui.switch.adult")}
          />
          <span className="min-w-0">
            <span className="block break-words text-sm font-medium">{t("roleui.switch.adult")}</span>
            <span className="block break-words text-xs text-muted-foreground">{t("roleui.switch.addHint")}</span>
          </span>
        </label>

        {account.roles.length >= MAX_ROLES && (
          <p className="break-words text-xs font-medium text-destructive">{t("roleui.manage.limit")}</p>
        )}

        <div className="flex flex-wrap gap-2">
          {missing.map((r) => {
            const Icon = ICONS[r];
            const check = canAddProfile(account.roles, account.isAdult, r);
            return (
              <Button
                key={r}
                size="sm"
                variant="secondary"
                className="min-h-9"
                disabled={busy || !check.ok}
                onClick={() => void run(() => manager.addProfile(r))}
              >
                <Icon className="mr-1.5 h-4 w-4" aria-hidden />
                <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
                {t(`teacher.role.${r}`)}
              </Button>
            );
          })}
        </div>
      </Card>

      <Card className="card-soft space-y-1 border-0 p-4">
        <h2 className="text-sm font-semibold">{t("roleui.manage.prefsTitle")}</h2>
        <p className="break-words text-xs text-muted-foreground">{t("roleui.manage.prefsHint")}</p>
      </Card>
    </div>
  );
}
